import { useEffect, useRef, useState } from "react";
/**
 * Custom hook to calculate the number of items that can fit in a row within a container.
 *
 * @param inputWidthPx - The width of each item in pixels.
 * @param gap - The gap between items in pixels. Defaults to 10.
 * @returns An object containing:
 * - `containerRef`: A ref to be attached to the container element.
 * - `itemsPerRow`: The calculated number of items that can fit in a row.
 */
const useItemsPerRow = (inputWidthPx: number, gap: number = 10) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [itemsPerRow, setItemsPerRow] = useState(1);

  const calculateItemsPerRow = () => {
    const container = containerRef.current;
    if (!container) return;

    // Give layout a moment to settle (ensures accurate width)
    requestAnimationFrame(() => {
      const containerWidth = container.offsetWidth;
      const totalItemWidth = inputWidthPx + gap;
      if (containerWidth && totalItemWidth) {
        const maxItems = Math.max(
          Math.floor((containerWidth + gap) / totalItemWidth),
          1,
        );
        setItemsPerRow(maxItems);
      }
    });
  };

  useEffect(() => {
    calculateItemsPerRow();

    const handleResize = () => {
      // Debounce resize to avoid excessive calls
      clearTimeout((handleResize as any)._timer);
      (handleResize as any)._timer = setTimeout(() => {
        calculateItemsPerRow();
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout((handleResize as any)._timer);
    };
  }, [inputWidthPx, gap]);

  return { containerRef, itemsPerRow };
};

export default useItemsPerRow;
